<template>
  <div
    :id="`details-card-${detail.checklistId}`"
    class="tw-bg-[#FCFCFC] tw-min-w-[284px] tw-max-w-[284px] tw-h-[112px] tw-w-[100%] tw-border tw-shadow-[0px_0px_4px_0px_rgba(0,0,0,0.05)] tw-p-5 tw-border-solid tw-border-[#EFF0F1] tw-rounded-[6px]"
  >
    <div class="tw-flex tw-flex-col tw-h-full tw-justify-between">
      <div class="tw-flex tw-justify-between tw-items-center wrapper">
        <span
          class="tw-text-[#211F58] tw-max-w-[138px] tw-text-[15px] tw-not-italic tw-font-[600] tw-leading-[130%] title"
        >
          {{
            getComponentUiText(
              "app-component-assessments-checklists-assessment-details-card-title-1",
            )
          }}
        </span>
        <div
          v-if="
            checklistTemplates &&
            checklistTemplates.length > 0 &&
            checklistTemplates[0]?.checklistTemplateName
          "
          :id="`checklist-template-name-detail-${detail.checklistId}`"
          class="default tw-px-[5px] tw-py-[3px] cell-status tw-flex tw-items-center tw-text-center tw-text-[11px] tw-font-[500]"
        >
          {{ checklistTemplates[0]?.checklistTemplateName }}

          <b-popover
            :target="`checklist-template-name-detail-${detail.checklistId}`"
            triggers="hover"
            placement="bottom"
            custom-class="custom-actions-popover"
            :offset="5"
            :delay="{
              show: 0,
              hide: 100,
            }"
            @show="true"
          >
            <div
              class="tw-px-[5px] tw-py-[3px] tw-bg-[#E4F1FF] tw-text-[#211F58] tw-rounded-[4px] tw-text-[11px] tw-font-medium tw-font-[Poppins]"
            >
              {{ checklistTemplates[0]?.checklistTemplateName }}
            </div>
          </b-popover>
        </div>
        <div
          v-else
          :id="`checklist-template-all-questions-detail-${detail.checklistId}`"
          class="default tw-px-[5px] tw-py-[3px] cell-status tw-flex tw-items-center tw-text-center tw-text-[11px] tw-font-[500]"
        >
          {{
            getComponentUiText(
              "app-component-assessments-checklists-assessment-template-tag-1",
            )
          }}
          <b-popover
            :target="`checklist-template-all-questions-detail-${detail.checklistId}`"
            triggers="hover"
            placement="bottom"
            custom-class="custom-actions-popover"
            :offset="5"
            :delay="{
              show: 0,
              hide: 100,
            }"
            @show="true"
          >
            <div
              class="tw-px-[5px] tw-py-[3px] tw-bg-[#E4F1FF] tw-text-[#211F58] tw-rounded-[4px] tw-text-[11px] tw-font-medium tw-font-[Poppins]"
            >
              {{
                getComponentUiText(
                  "app-component-assessments-checklists-assessment-template-tag-1",
                )
              }}
            </div>
          </b-popover>
        </div>
      </div>
      <div class="tw-flex align-items-end">
        <div class="detail-info">
          <div class="tw-text-[#211F58] tw-text-[13px] tw-font-[500]">
            <div
              v-show="isShowFlagCard"
              class="tw-flex tw-justify-between tw-items-center"
            >
              {{
                getComponentUiText(
                  "app-component-assessments-checklists-assessment-details-card-label-2",
                )
              }}

              <div
                v-if="isChecklistInfoDetailLoading && !isAssessmentDetail"
                class="custom-global-skeleton tw-w-[122px] tw-h-[19px]"
              ></div>
              <span v-else class="tw-text-[#878787] tw-font-[400]">{{
                getLastUpdatedTimeFromNow()
              }}</span>
            </div>
            <div
              v-show="!isShowFlagCard"
              class="tw-flex tw-justify-between tw-items-center"
            >
              {{
                getComponentUiText(
                  "app-component-assessments-checklists-assessment-details-card-label-3",
                )
              }}
              <div
                v-if="isChecklistInfoDetailLoading && !isAssessmentDetail"
                class="custom-global-skeleton tw-w-[122px] tw-h-[19px]"
              ></div>
              <div v-else class="tw-text-[#878787] tw-font-[400]">
                {{ getLastUpdatedTimeFromNow() }}
              </div>
            </div>
          </div>
          <div
            class="tw-text-[#211F58] tw-text-[13px] tw-font-[500] tw-flex tw-justify-between tw-items-center"
          >
            {{
              getComponentUiText(
                "app-component-assessments-checklists-assessment-details-card-label-1",
              )
            }}
            <div
              v-if="isChecklistInfoDetailLoading && !isAssessmentDetail"
              class="custom-global-skeleton tw-w-[122px] tw-h-[19px]"
            ></div>
            <span v-else class="tw-text-[#878787] tw-font-[400]">{{
              getCurrentTime()
            }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getComponentUiText, getShortChecklistName } from "@/utils/utilities";
import {
  convertUTCTimeToLocalTime,
  getTimeFromNow,
} from "@/utils/dateTimeUtils";
import { checklistStatusId } from "@/utils/constants";
import { computed, onMounted, ref } from "vue";
import store from "@/store";
import { getDetailFromCheckListId } from "@/services/assessment";
import get from "lodash/get";
import { IChecklistInfoDetail } from "@/interfaces/assessments/IAssessmentDetails";
import { showErrorAPI } from "@/services/apiRoutes";
import { Checklist } from "@/services/apiRoutes/routes";

const props = defineProps({
  isAssessmentDetail: {
    type: Boolean,
    required: false,
    default: false,
  },
  detail: {
    type: Object,
    required: false,
    default: () => ({}),
  },
});

onMounted(() => {
  getChecklistDetails();
});

const checklistLastUpdatedDate = ref("");

const checklistInfoDetail = computed<any>(() => {
  if (props.isAssessmentDetail) {
    const assessment = store.getters["Recommend/getChecklistDetailById"](
      props.detail.checklistId,
    );
    return {
      checklistCreatedDate: assessment.checklistDateCreated,
      checklistLastUpdatedDate: assessment.checklistDateEdited,
      checklistStatusId: assessment.checklistStatusId,
      checklistStatus: assessment.checklistStatus,
      checklistId: props.detail.checklistId,
    };
  } else {
    return store.getters["Assessment/getChecklistInforDetail"];
  }
});

const checklistTemplates = computed(() => {
  return store.getters["Recommend/getChecklistDetailById"](
    checklistInfoDetail.value?.checklistId,
  ).templates;
});

const currentChecklistId = computed(() => {
  return store.state.Assessment.currentChecklistId;
});

const getChecklistDetails = async () => {
  try {
    const res = await getDetailFromCheckListId(
      Number(props.detail.checklistId) || currentChecklistId.value,
    );
    const data = get(res, "data.data", {}) as IChecklistInfoDetail;
    checklistLastUpdatedDate.value = data.checklistLastUpdatedDate;
  } catch (error: any) {
    showErrorAPI(error, Checklist.getChecklistToItemMetric);
  }
};

const getLastUpdatedTimeFromNow = () => {
  if (!checklistLastUpdatedDate.value) return "-";
  return getTimeFromNow(checklistLastUpdatedDate.value);
};

const getCurrentTime = () => {
  if (!checklistInfoDetail.value.checklistCreatedDate) return "-";
  return convertUTCTimeToLocalTime(
    checklistInfoDetail.value.checklistCreatedDate,
    undefined,
    "HH:mm",
  );
};

const isShowFlagCard = computed(() => {
  return (
    checklistInfoDetail.value.checklistStatusId !== checklistStatusId.completed
  );
});

const isChecklistInfoDetailLoading = computed(() => {
  return Object.keys(checklistInfoDetail.value).length === 0;
});
</script>

<style scoped>
.detail-info {
  flex: 1;
}
.default {
  background: var(--ui-white, #fff);
  color: var(--ui-dark-grey, #878787);
  border-radius: 4px;
  border: 1px solid var(--ui-rule, #eff0f1);
  display: inline-block;
  max-width: 50%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: top;
}

.inProgress {
  background: linear-gradient(
      0deg,
      rgba(72, 160, 255, 0.15) 0%,
      rgba(72, 160, 255, 0.15) 100%
    ),
    #fff;
  color: #387dff;
}

.notStarted {
  background: var(--ui-orange-soft, #fee5e2);
  color: #f7553c;
}

.complete {
  background: var(--ui-green-soft, #d9ede2);
  color: #01893d;
}

.audited {
  background: linear-gradient(
      0deg,
      rgba(144, 75, 214, 0.15) 0%,
      rgba(144, 75, 214, 0.15) 100%
    ),
    #fff;
  color: #904bd6;
}

:deep(.bs-popover-bottom > .popover-arrow)::after {
  border-bottom-color: #e4f1ff;
}
</style>
